import type { AuthUser } from "@playbrain/auth/dashboardRoles"
import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react"
import {
  performLogoutAction,
  performSessionRefreshAction,
  requestSignInCodeAction,
  startGoogleSignInAction,
  submitSignInCodeAction,
} from "./actions"
import { isDashboardAuthBypassEnabled } from "./config"
import { readServerSession } from "./sessionApi"
import { clearTransaction } from "./transactions"
import type { AuthSession, DashboardAuthContextValue } from "./types"
import { errorMessage } from "./utils"

const DashboardAuthContext = createContext<DashboardAuthContextValue | null>(null)

/**
 * `initialSession` is the session the server already resolved for this request.
 *
 * Without it the provider starts in a loading state and every consumer — the
 * gate, the nav, the permission checks — has to wait a client round-trip before
 * anything can render, which is what put a full-page skeleton in front of the
 * dashboard on every cold load. Supplied, the first paint already knows who you
 * are and the skeleton never appears; the background refresh below still runs,
 * so nothing about session freshness changes.
 */
export function DashboardAuthProvider({
  children,
  initialSession = null,
}: PropsWithChildren<{ initialSession?: AuthSession | null }>) {
  const [session, setSession] = useState<AuthSession | null>(initialSession)
  /**
   * The mount-time "who are you?" round-trip, kept apart from the flag below.
   *
   * One `isLoading` used to mean both this and "the thing you just clicked is
   * in flight", and the login page reads that flag to put a spinner in its
   * submit button and disable its fields. That page mounts the provider with
   * no `initialSession`, so every cold load of `/login` -- most visibly the one
   * you land on right after signing out -- opened with the form already
   * spinning, reporting a submission nobody had made yet.
   */
  const [isRestoringSession, setIsRestoringSession] = useState(initialSession === null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLoggingOut, setIsLoggingOut] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const localDevUser = useMemo<AuthUser | null>(() => {
    if (!isDashboardAuthBypassEnabled()) return null
    return {
      sub: "local-dev",
      dashboardLanguage: "en",
      email: "local-dev@playbrain.com",
      name: "Local Dev",
      username: "Local Dev",
      user_metadata: { roles: ["admin"] },
    }
  }, [])

  useEffect(() => {
    if (localDevUser) {
      setIsRestoringSession(false)
      return
    }

    let active = true
    readServerSession()
      .then((restored) => {
        if (!active) return
        setSession(restored)
      })
      .catch(() => {
        if (!active) return
        setSession(null)
      })
      .finally(() => {
        if (active) setIsRestoringSession(false)
      })

    return () => {
      active = false
    }
  }, [localDevUser])

  const clearError = useCallback(() => {
    setError(null)
  }, [])

  const applySession = useCallback((nextSession: AuthSession | null) => {
    setSession(nextSession)
  }, [])

  /**
   * Ask for a sign-in code.
   *
   * Deliberately does NOT set an error when the server declines to send. It
   * answers 200 for an unknown address, because the dashboard has no signup and
   * a screen that confirmed which addresses exist would be a staff directory --
   * so there is nothing here to report, and reporting something would undo that.
   */
  const requestSignInCode = useCallback(async (email: string) => {
    setIsSubmitting(true)
    setError(null)
    try {
      await requestSignInCodeAction(email)
    } catch (caughtError) {
      setError(errorMessage(caughtError, "Could not send a code."))
      throw caughtError
    } finally {
      setIsSubmitting(false)
    }
  }, [])

  /**
   * Redeem a code.
   *
   * On success `isSubmitting` is left true, exactly as `startGoogleSignIn`
   * leaves it, and for the same reason: a valid code means the login page is
   * already replacing the document with `returnTo`. Clearing it here handed
   * back an idle, interactive form for the length of that navigation — the
   * same mismatch as a spinner on a form nobody submitted, just pointed the
   * other way. Only a rejected code returns you to a usable form.
   */
  const submitSignInCode = useCallback(
    async (email: string, code: string) => {
      setIsSubmitting(true)
      setError(null)
      try {
        applySession(await submitSignInCodeAction(email, code))
      } catch (caughtError) {
        setSession(null)
        setError(errorMessage(caughtError, "That code is not valid."))
        setIsSubmitting(false)
        throw caughtError
      }
    },
    [applySession]
  )

  /**
   * Begins Google sign-in and hands the window over.
   *
   * `isSubmitting` is deliberately left true on success: the navigation is
   * already under way, and clearing it would flash an interactive form the
   * person can no longer usefully use.
   */
  const startGoogleSignIn = useCallback(async (returnTo?: string) => {
    setIsSubmitting(true)
    setError(null)
    try {
      await startGoogleSignInAction(returnTo)
    } catch (caughtError) {
      setError(errorMessage(caughtError, "Could not start Google sign-in."))
      setIsSubmitting(false)
      throw caughtError
    }
  }, [])

  const refreshSession = useCallback(async () => {
    if (localDevUser) return null
    const nextSession = await performSessionRefreshAction()
    applySession(nextSession)
    return nextSession
  }, [applySession, localDevUser])

  useEffect(() => {
    if (localDevUser || !session || typeof window === "undefined") return

    const refreshCurrentSession = () => {
      void refreshSession().catch(() => undefined)
    }
    const refreshVisibleSession = () => {
      if (typeof document !== "undefined" && document.visibilityState !== "visible") return
      refreshCurrentSession()
    }

    window.addEventListener("focus", refreshCurrentSession)
    document.addEventListener("visibilitychange", refreshVisibleSession)
    return () => {
      window.removeEventListener("focus", refreshCurrentSession)
      document.removeEventListener("visibilitychange", refreshVisibleSession)
    }
  }, [localDevUser, refreshSession, session])

  const getAccessToken = useCallback(async () => {
    await refreshSession()
    return null
  }, [refreshSession])

  /**
   * Signs out and hands the window over — deliberately without tearing the
   * page down first.
   *
   * This used to clear the session immediately, which pulled the dashboard out
   * from under the person and left the gate rendering a placeholder at whoever
   * asked for one. That is the thing no ordinary site does: a sign-out button
   * is a form submit, and the page you are on stays up until the next one
   * paints. So the session stays, the account dialog's button goes pending,
   * and the browser does the transition it was always going to do.
   *
   * The in-memory session outliving the click by a few hundred milliseconds
   * costs nothing: the cookie is what authorises anything, the server is
   * revoking it as we speak, and we leave the moment it answers.
   */
  const logout = useCallback(() => {
    setIsLoggingOut(true)
    setError(null)
    clearTransaction()
    void performLogoutAction()
      .catch(() => null)
      .finally(() => window.location.replace("/login"))
  }, [])

  return (
    <DashboardAuthContext.Provider
      value={{
        isAuthenticated: Boolean(localDevUser || session),
        /**
         * "The auth layer is busy with something you have to wait out" -- what
         * the gate needs to decide whether to hold a page back.
         *
         * `isLoggingOut` is deliberately NOT in here. Signing out is the one
         * wait that must leave the page alone: hiding the dashboard behind a
         * placeholder on the way out is what made a sign-out look like a page
         * load. Surfaces that want to show the sign-out itself read
         * `isLoggingOut` directly.
         */
        isLoading: isSubmitting || isRestoringSession,
        isRestoringSession,
        isSubmitting,
        isLoggingOut,
        /**
         * Constant by design. Sign-in is now a set of same-origin server
         * routes with no client-side configuration -- the Auth0 SPA settings
         * this used to reflect are gone, and a server that is missing its own
         * Supabase config answers requests with an error the form already
         * shows. Keeping the flag (rather than deleting it) spares every
         * consumer a migration for what is now an invariant.
         */
        isConfigured: true,
        session: localDevUser ? null : session,
        user: localDevUser ?? session?.user ?? null,
        error,
        requestSignInCode,
        submitSignInCode,
        startGoogleSignIn,
        logout,
        getAccessToken,
        refreshSession,
        clearError,
      }}
    >
      {children}
    </DashboardAuthContext.Provider>
  )
}

export function useDashboardAuth() {
  const context = useContext(DashboardAuthContext)
  if (!context) throw new Error("DashboardAuthProvider is missing.")
  return context
}
